import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { Btn } from "../../../../AbstractElements";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";

interface TagFormValues {
  name: string;
}

const tagValidation = Yup.object().shape({
  name: Yup.string()
    .required("Tag name is required")
    .min(2, "Tag name must be at least 2 characters")
    .max(100, "Tag name must be less than 100 characters"),
});

const CreateTagForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams(); // expects route like /tags/edit/:id

  const isEdit = location.pathname.includes("/edit");

  const [tagInitialValue, setTagInitialValue] = useState<TagFormValues>({
    name: "",
  });
  const [loading, setLoading] = useState(false);

  // Fetch tag if edit
  useEffect(() => {
    const fetchTag = async () => {
      if (!isEdit || !id) return;

      try {
        setLoading(true);
        const res = await apiRequestHelper.get(`/tags/${id}`);

        // Adjust mapping if your API shape differs:
        const tag = res.data?.data?.tag || res.data?.data;

        setTagInitialValue({
          name: tag?.name ?? "",
        });
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Failed to load tag");
      } finally {
        setLoading(false);
      }
    };

    fetchTag();
  }, [isEdit, id]);

  const handleSubmit = async (values: TagFormValues) => {
    try {
      setLoading(true);

      if (isEdit && id) {
        await apiRequestHelper.put(`/tags/${id}`, values);
        toast.success("Tag updated successfully!");
      } else {
        await apiRequestHelper.post("/tags", values);
        toast.success("Tag created successfully!");
      }

      navigate("/tags/cards");
    } catch (error: any) {
      toast.error(
        error.response?.data?.messages?.[0]?.message ||
          error.response?.data?.message ||
          "Request failed!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={tagInitialValue}
      validationSchema={tagValidation}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form className="theme-form">
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>
                  Tag Name <span className="txt-danger">*</span>
                </Label>
                <input
                  className={`form-control ${
                    errors.name && touched.name ? "is-invalid" : ""
                  }`}
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter tag name (e.g., Dental Care, Invisalign)"
                  disabled={loading}
                />
                {errors.name && touched.name && (
                  <div className="invalid-feedback d-block">{errors.name}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="text-end">
                <Btn
                  color="secondary"
                  className="me-2"
                  onClick={() => navigate("/tags/cards")}
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </Btn>

                <Btn
                  className="btn-edit-custom "
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Please wait..."
                    : isEdit
                    ? "Update Tag"
                    : "Create Tag"}
                </Btn>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default CreateTagForm;
